// src/pages/PurchaseInvoices.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw, Search, ShoppingCart, ChevronLeft, ChevronRight,
  Filter, X, FolderOpen,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { purchaseInvoicesAPI, projectsAPI } from '../services/api';
import { formatAmount } from '../utils/currency';
import './TimesheetDetail.css';

const PER_PAGE = 25;

const STATUTS = [
  { value: 'EN_ATTENTE', label: 'En attente', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
  { value: 'VALIDEE',    label: 'Validée',    color: '#3B82F6', bg: 'rgba(59,130,246,0.10)' },
  { value: 'PAYEE',      label: 'Payée',      color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  { value: 'ANNULEE',    label: 'Annulée',    color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
];

const TYPES = [
  { value: 'ACHAT',              label: "Facture d'achat" },
  { value: 'AVOIR_FOURNISSEUR',  label: 'Avoir fournisseur' },
];

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('fr-FR') : '—';
}

function StatutBadge({ statut }) {
  const s = STATUTS.find(x => x.value === statut);
  if (!s) return <span style={{ color: 'var(--text-faint)', fontSize: '0.8125rem' }}>{statut || '—'}</span>;
  return (
    <span className="badge" style={{ color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '12px 16px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-faint)' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.15rem', fontWeight: 700, marginTop: 4, color: accent || 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

export default function PurchaseInvoices() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ statut: '', type_facture: '', projet_id: '' });
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (search) params.q = search;
      if (filters.statut) params.statut = filters.statut;
      if (filters.type_facture) params.type_facture = filters.type_facture;
      if (filters.projet_id) params.projet_id = filters.projet_id;
      const { data } = await purchaseInvoicesAPI.list(params);
      setItems(data.items || []);
      setTotaux(data.totaux || null);
      setPage(1);
    } catch {
      toast.error("Erreur chargement des factures d'achats");
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  useEffect(() => {
    projectsAPI.list({ limit: 500 })
      .then(({ data }) => setProjects(data.items || data || []))
      .catch(() => {});
  }, []);

  const hasFilters = Object.values(filters).some(Boolean);
  const totalPages = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const pageItems = items.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Factures d'achats</h1>
          <p>Factures fournisseurs de tous les projets — vue consolidée</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowFilters(s => !s)}>
            <Filter size={15}/> Filtres
            {hasFilters && (
              <span style={{ marginLeft:4, background:'#3B82F6', color:'white', borderRadius:'50%',
                width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem' }}>
                {Object.values(filters).filter(Boolean).length}
              </span>
            )}
          </button>
          <button className="btn btn-secondary" onClick={fetchData}><RefreshCw size={15}/></button>
        </div>
      </div>

      {totaux && (
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          <Kpi label="Total HT"  value={formatAmount(totaux.total_ht)} />
          <Kpi label="Total TTC" value={formatAmount(totaux.total_ttc)} />
          <Kpi label="Payé"      value={formatAmount(totaux.montant_paye)} accent="#16A34A" />
          <Kpi label="Impayé"    value={formatAmount(totaux.montant_impaye)} accent={totaux.montant_impaye > 0 ? '#F59E0B' : undefined} />
        </div>
      )}

      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <div style={{ position:'relative', flex:1 }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input
            style={{ paddingLeft:34, height:38, border:'1px solid var(--border)', borderRadius:8, width:'100%', background:'var(--bg-card)', color:'var(--text-primary)', outline:'none', boxSizing:'border-box' }}
            placeholder="Réf. externe, fournisseur, description, intervenants…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {(search || hasFilters) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilters({ statut:'', type_facture:'', projet_id:'' }); }}>
            <X size={14}/> Réinitialiser
          </button>
        )}
      </div>

      {showFilters && (
        <div className="card" style={{ marginBottom:16, padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            <div>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-faint)', display:'block', marginBottom:4 }}>Statut</label>
              <select className="filter-select" style={{ width:'100%' }} value={filters.statut}
                      onChange={e => setFilters(f => ({ ...f, statut: e.target.value }))}>
                <option value="">Tous</option>
                {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-faint)', display:'block', marginBottom:4 }}>Type</label>
              <select className="filter-select" style={{ width:'100%' }} value={filters.type_facture}
                      onChange={e => setFilters(f => ({ ...f, type_facture: e.target.value }))}>
                <option value="">Tous</option>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'0.75rem', fontWeight:600, color:'var(--text-faint)', display:'block', marginBottom:4 }}>Projet</label>
              <select className="filter-select" style={{ width:'100%' }} value={filters.projet_id}
                      onChange={e => setFilters(f => ({ ...f, projet_id: e.target.value }))}>
                <option value="">Tous</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card" style={{ textAlign:'center', padding:'48px 0', color:'var(--text-faint)' }}>Chargement…</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px 0', color:'var(--text-faint)' }}>
          <ShoppingCart size={32} style={{ marginBottom:12, opacity:0.4 }}/>
          <div style={{ fontSize:'0.9375rem', fontWeight:600 }}>Aucune facture d'achat</div>
          <div style={{ fontSize:'0.8125rem', marginTop:6 }}>
            Les factures fournisseurs se saisissent dans l'onglet « Factures d'achats » d'un projet.
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap" style={{ overflowX:'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Réf. externe</th>
                  <th>Type</th>
                  <th>Fournisseur</th>
                  <th>Projet</th>
                  <th>Date</th>
                  <th style={{ textAlign:'right' }}>Montant HT</th>
                  <th style={{ textAlign:'right' }}>TVA</th>
                  <th style={{ textAlign:'right' }}>Montant TTC</th>
                  <th style={{ textAlign:'right' }}>Payé</th>
                  <th style={{ textAlign:'right' }}>Impayé</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map(inv => (
                  <tr key={inv.id}>
                    <td>
                      <button className="pd-link" style={{ fontWeight:600 }}
                              onClick={() => navigate(`/purchase-invoices/${inv.id}`)}>
                        {inv.ref_externe || 'Sans référence'}
                      </button>
                    </td>
                    <td>{(TYPES.find(t => t.value === inv.type_facture) || {}).label || inv.type_facture}</td>
                    <td>{inv.emetteur || '—'}</td>
                    <td>
                      {inv.projet_nom ? (
                        <button className="pd-link" onClick={() => navigate(`/projects/${inv.project_id}`)}
                                style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                          <FolderOpen size={12}/> {inv.projet_nom}
                        </button>
                      ) : '—'}
                    </td>
                    <td>{fmtDate(inv.date_facture)}</td>
                    <td style={{ textAlign:'right' }}>{formatAmount(inv.montant_ht)}</td>
                    <td style={{ textAlign:'right' }}>{inv.taux_tva} %</td>
                    <td style={{ textAlign:'right', fontWeight:600 }}>{formatAmount(inv.montant_ttc)}</td>
                    <td style={{ textAlign:'right' }}>{formatAmount(inv.montant_paye)}</td>
                    <td style={{ textAlign:'right', color: inv.montant_impaye > 0 ? '#F59E0B' : undefined }}>
                      {formatAmount(inv.montant_impaye)}
                    </td>
                    <td><StatutBadge statut={inv.statut}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, padding:'10px 14px' }}>
              <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={14}/>
              </button>
              <span style={{ fontSize:'0.8125rem', color:'var(--text-faint)' }}>{page} / {totalPages}</span>
              <button className="btn btn-ghost btn-sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight size={14}/>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
